import { useState, useEffect, useCallback, useRef } from 'react'
import PartySocket from 'partysocket'
import { useStore } from '../store/useStore'

const PARTYKIT_HOST = import.meta.env.VITE_PARTYKIT_HOST || 'p5ai-social-coding.partykit.dev'

export function usePartyKit(roomId, user) {
  const [socket, setSocket] = useState(null)
  const [isConnected, setIsConnected] = useState(false)
  const [users, setUsers] = useState([])
  const [promptHistory, setPromptHistory] = useState([])
  const reconnectAttempts = useRef(0)
  const maxReconnectAttempts = 5

  // Connect to room
  useEffect(() => {
    if (!roomId || !user) return

    const ws = new PartySocket({
      host: PARTYKIT_HOST,
      room: roomId,
    })

    ws.addEventListener('open', () => {
      setIsConnected(true)
      reconnectAttempts.current = 0
      
      // Send join message
      ws.send(JSON.stringify({
        type: 'join',
        user: {
          odId: user.odId || user.id,
          username: user.username || user.firstName || 'Anonymous',
          color: user.color || null
        }
      }))
    })

    ws.addEventListener('message', (event) => {
      try {
        const data = JSON.parse(event.data)
        handleMessage(data)
      } catch (e) {
        console.error('[PartyKit] Error parsing message:', e)
      }
    })

    ws.addEventListener('close', () => {
      setIsConnected(false)
      
      // Attempt reconnect
      if (reconnectAttempts.current < maxReconnectAttempts) {
        reconnectAttempts.current++
        console.log(`[PartyKit] Reconnecting (attempt ${reconnectAttempts.current})...`)
      }
    })

    ws.addEventListener('error', (error) => {
      console.error('[PartyKit] Socket error:', error)
    })

    setSocket(ws)

    return () => {
      // Send leave message before disconnecting
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          type: 'leave',
          odId: user.odId || user.id
        }))
      }
      ws.close()
      setSocket(null)
      setIsConnected(false)
    }
  }, [roomId, user])

  const handleMessage = useCallback((data) => {
    switch (data.type) {
      case 'sync-response':
        // Initial sync - set all state
        if (data.state.files) {
          const store = useStore.getState()
          const currentFiles = store.files
          
          // Merge remote files with local
          Object.entries(data.state.files).forEach(([fileName, content]) => {
            const existingFile = currentFiles.find(f => f.name === fileName)
            if (existingFile) {
              store.updateFileContent(fileName, content, false)
            }
          })
        }
        setUsers(Object.values(data.state.users || {}))
        setPromptHistory(data.state.promptHistory || [])
        break

      case 'user-list':
        setUsers(data.users)
        break

      case 'file-update':
        // Apply remote file change
        useStore.getState().updateFileContent(data.fileName, data.content, false)
        break

      case 'prompt':
        setPromptHistory(prev => [...prev, {
          userId: data.userId,
          username: data.username,
          prompt: data.prompt,
          timestamp: data.timestamp
        }])
        break

      case 'cursor':
        // Could dispatch cursor event for visual indicator
        break
    }
  }, [])

  // Send file update
  const sendFileUpdate = useCallback((fileName, content) => {
    if (!socket || !isConnected || !user) return
    
    socket.send(JSON.stringify({
      type: 'file-update',
      fileName,
      content,
      userId: user.odId || user.id
    }))
  }, [socket, isConnected, user])

  // Send prompt
  const sendPrompt = useCallback((prompt) => {
    if (!socket || !isConnected || !user) return
    
    socket.send(JSON.stringify({
      type: 'prompt',
      prompt,
      userId: user.odId || user.id,
      username: user.username || user.firstName || 'Anonymous'
    }))
  }, [socket, isConnected, user])

  // Send cursor position
  const sendCursor = useCallback((fileName, line, column) => {
    if (!socket || !isConnected || !user) return
    
    socket.send(JSON.stringify({
      type: 'cursor',
      userId: user.odId || user.id,
      fileName,
      line,
      column
    }))
  }, [socket, isConnected, user])

  // Request sync
  const requestSync = useCallback(() => {
    if (!socket || !isConnected) return
    
    socket.send(JSON.stringify({ type: 'sync-request' }))
  }, [socket, isConnected])

  return {
    isConnected,
    users,
    promptHistory,
    sendFileUpdate,
    sendPrompt,
    sendCursor,
    requestSync
  }
}

// Helper to create/initialize a room
export async function createRoom(roomId, name, tags, createdBy) {
  const response = await fetch(`https://${PARTYKIT_HOST}/party/${roomId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, tags, createdBy })
  })
  return response.json()
}

// Helper to get room info
export async function getRoomInfo(roomId) {
  const response = await fetch(`https://${PARTYKIT_HOST}/party/${roomId}`)
  return response.json()
}
